/**
 * アカウントのセキュリティ（認証アプリの登録・再登録）
 *
 * 本人確認として、直前のサインイン（MFA を含む）から 10 分以内であることを求める（要件 4.1）。
 * 乗っ取られた古いセッションから認証アプリを差し替えられるのを防ぐため。
 * MFA を解除する操作は提供しない（要件 4.2）。
 */

import {
  fetchAuthSession,
  fetchMFAPreference,
  setUpTOTP,
  updateMFAPreference,
  verifyTOTPSetup,
} from 'aws-amplify/auth';
import { useCallback, useEffect, useState } from 'react';
import { classifyAuthError, type SignInFlowError } from './classify-auth-error';
import { getTotpIssuer } from './use-sign-in-flow';

/** 登録・再登録に必要な、直前のサインインからの経過時間の上限 */
export const REAUTH_WINDOW_MS = 10 * 60 * 1000;

/**
 * replacing：登録済みの認証アプリを置き換える再登録か。登録状態を取得できなかった場合は、
 * 古い登録を残すよう促す側に倒して再登録として扱う
 */
export type AccountSecurityState =
  | { kind: 'loading' }
  | { kind: 'reauthRequired' }
  /** totpEnabled が null のときは、登録状態を取得できなかった */
  | { kind: 'idle'; totpEnabled: boolean | null }
  | { kind: 'enrolling'; setupUri: string; sharedSecret: string; replacing: boolean }
  /** 認証アプリのコードは確認できたが、優先方式の設定に失敗した */
  | { kind: 'preferencePending'; replacing: boolean }
  | { kind: 'enrolled'; replacing: boolean };

export interface AccountSecurity {
  state: AccountSecurityState;
  error: SignInFlowError | null;
  submitting: boolean;
  startEnrollment(): Promise<void>;
  submitCode(code: string): Promise<void>;
  /** 優先方式の設定だけをやり直す（コードの確認はやり直さない） */
  retryPreference(): Promise<void>;
  /** 登録を中断する。確認前なので、これまでの認証アプリのコードが引き続き有効 */
  cancelEnrollment(): Promise<void>;
  /** 登録状態を取得し直して、その表示に戻る */
  reload(): Promise<void>;
}

/** 直前のサインインの情報。サインイン時刻が分からなければ null */
async function loadRecentSignIn(): Promise<{ email: string } | null> {
  const session = await fetchAuthSession();
  const payload = session.tokens?.idToken?.payload;
  const authTime = payload?.auth_time;
  if (typeof authTime !== 'number' || Date.now() - authTime * 1000 > REAUTH_WINDOW_MS) {
    return null;
  }
  return { email: typeof payload?.email === 'string' ? payload.email : '' };
}

export function useAccountSecurity(): AccountSecurity {
  const [state, setState] = useState<AccountSecurityState>({ kind: 'loading' });
  const [error, setError] = useState<SignInFlowError | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const replacing = state.kind === 'idle' ? state.totpEnabled !== false : true;

  const reload = useCallback(async () => {
    setError(null);
    setState({ kind: 'loading' });
    try {
      if (!(await loadRecentSignIn())) {
        setState({ kind: 'reauthRequired' });
        return;
      }
      const preference = await fetchMFAPreference();
      setState({ kind: 'idle', totpEnabled: preference.enabled?.includes('TOTP') ?? false });
    } catch (err) {
      // 取得できなかったことを「未登録」と見せると、登録が消えたと誤解させる
      setError(classifyAuthError(err));
      setState({ kind: 'idle', totpEnabled: null });
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const startEnrollment = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      // 画面を開いたまま時間が経っていることがあるため、開始時にも確かめる
      const recent = await loadRecentSignIn();
      if (!recent) {
        setState({ kind: 'reauthRequired' });
        return;
      }
      const details = await setUpTOTP();
      setState({
        kind: 'enrolling',
        setupUri: details.getSetupUri(getTotpIssuer(), recent.email).toString(),
        sharedSecret: details.sharedSecret,
        replacing,
      });
    } catch (err) {
      setError(classifyAuthError(err));
    } finally {
      setSubmitting(false);
    }
  }, [replacing]);

  /** 優先方式を設定する。失敗したら設定だけを再試行できる状態にする */
  const applyPreference = useCallback(async (replacing: boolean) => {
    try {
      await updateMFAPreference({ totp: 'PREFERRED' });
      setState({ kind: 'enrolled', replacing });
    } catch (err) {
      setError(classifyAuthError(err));
      setState({ kind: 'preferencePending', replacing });
    }
  }, []);

  const submitCode = useCallback(
    async (code: string) => {
      if (state.kind !== 'enrolling') return;
      const { replacing } = state;
      setSubmitting(true);
      setError(null);
      try {
        // 検証に成功した時点で新しいシークレットに置き換わる（それまでは旧シークレットが有効）
        await verifyTOTPSetup({ code });
      } catch (err) {
        setError(classifyAuthError(err));
        setSubmitting(false);
        return;
      }
      await applyPreference(replacing);
      setSubmitting(false);
    },
    [applyPreference, state],
  );

  const retryPreference = useCallback(async () => {
    if (state.kind !== 'preferencePending') return;
    setSubmitting(true);
    setError(null);
    await applyPreference(state.replacing);
    setSubmitting(false);
  }, [applyPreference, state]);

  const cancelEnrollment = useCallback(async () => {
    if (state.kind !== 'enrolling') return;
    await reload();
  }, [reload, state.kind]);

  return {
    state,
    error,
    submitting,
    startEnrollment,
    submitCode,
    retryPreference,
    cancelEnrollment,
    reload,
  };
}
